import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
const PROJECTS = [{
  id: 1,
  image: 'https://cdn.magicpatterns.com/uploads/baMH1LsVQMA7z6bDs7mEVq/gimnasio.jpg',
  title: 'Sitio web para gimnasio',
  description: 'Diseño profesional para club deportivo con enfoque en membresías, entrenamientos y captación de nuevos clientes.'
}, {
  id: 2,
  image: 'https://cdn.magicpatterns.com/uploads/mBRVc7NRwtJHrGXnTaD5gH/restaurante.jpg',
  title: 'Web para restaurante',
  description: 'Sitio dinámico para negocio gastronómico con menú visual, presentación de productos y consultas online.'
}, {
  id: 3,
  image: 'https://cdn.magicpatterns.com/uploads/mcibH3djKrY3HqRvA1dE59/agencia.jpg',
  title: 'Landing para agencia creativa',
  description: 'Página moderna para marca digital con foco en identidad visual, marketing y presentación de servicios creativos.'
}, {
  id: 4,
  image: 'https://cdn.magicpatterns.com/uploads/9mtq8ruhPYHEvKm22JbYtV/estetica.jpg',
  title: 'Web para centro de estética',
  description: 'Diseño orientado a mostrar servicios de belleza, generar confianza y facilitar reservas de turnos.'
}];
export const Portfolio: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const closeModal = () => setSelectedImage(null);
  // ESC key + body scroll lock
  useEffect(() => {
    if (!selectedImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow || '';
    };
  }, [selectedImage]);
  return <section id="portfolio" className="py-24 relative z-10" data-id="element-126">
      <div className="max-w-7xl mx-auto px-6" data-id="element-127">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-128">
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-129">
            Trabajos realizados por Impulsador Digital
          </h2>
          <p className="text-xl text-silver max-w-2xl mx-auto" data-id="element-130">
            Algunos diseños creados para negocios reales y proyectos digitales.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16" data-id="element-131">
          {PROJECTS.map((project, index) => <motion.div key={project.id} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="group flex flex-col" data-id="element-132">
              {/* Laptop Mockup */}
              <div className="relative w-full pt-[60%] mb-6 rounded-t-2xl rounded-b-md bg-dark-surface border-[8px] border-dark-surface shadow-2xl overflow-hidden transition-all duration-500 group-hover:border-neon-blue/50 group-hover:shadow-[0_0_30px_rgba(0,207,255,0.2)]" data-id="element-133">
                <div className="absolute inset-0 bg-black overflow-hidden" data-id="element-134">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" data-id="element-135" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center" data-id="element-136">
                    <button type="button" onClick={() => setSelectedImage(project.image)} className="w-16 h-16 rounded-full bg-neon-blue/90 text-white flex items-center justify-center transform scale-50 group-hover:scale-100 transition-all duration-300 delay-100 box-glow" aria-label="Ver diseño ampliado" data-id="element-137">
                      <ExternalLink size={24} data-id="element-138" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="h-4 bg-gradient-to-b from-gray-800 to-gray-900 rounded-b-xl w-[105%] -ml-[2.5%] relative shadow-xl mb-6" data-id="element-139">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/4 h-1 bg-gray-700 rounded-b-md" data-id="element-140" />
              </div>

              <div className="flex-grow flex flex-col" data-id="element-141">
                <h3 className="text-2xl font-display font-bold mb-3 text-white group-hover:text-neon-blue transition-colors" data-id="element-142">
                  {project.title}
                </h3>
                <p className="text-silver mb-6 flex-grow" data-id="element-143">
                  {project.description}
                </p>
                <button type="button" onClick={() => setSelectedImage(project.image)} className="self-start text-sm font-bold tracking-wider uppercase text-white border-b-2 border-neon-blue pb-1 hover:text-neon-blue transition-colors" data-id="element-144">
                  Ver diseño
                </button>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence data-id="element-145">
        {selectedImage && <motion.div key="modal-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 flex items-center justify-center p-4 sm:p-8 bg-black/90 backdrop-blur-sm" style={{
        zIndex: 9999
      }} onClick={closeModal} role="dialog" aria-modal="true" aria-label="Vista ampliada del diseño" data-id="element-146">
            {/* Close button — fixed to viewport, always on top */}
            <button type="button" onClick={e => {
          e.stopPropagation();
          closeModal();
        }} className="fixed top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 bg-dark-surface border border-white/20 rounded-full flex items-center justify-center text-white hover:text-neon-blue hover:border-neon-blue hover:bg-white/10 transition-colors shadow-[0_0_20px_rgba(0,207,255,0.3)]" style={{
          zIndex: 10000
        }} aria-label="Cerrar modal" data-id="element-147">
              <X size={28} strokeWidth={2.5} data-id="element-148" />
            </button>

            <motion.div key="modal-content" initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative max-w-6xl w-full max-h-[90vh] rounded-xl overflow-hidden shadow-[0_0_50px_rgba(0,207,255,0.15)]" onClick={e => e.stopPropagation()} data-id="element-149">
              <img src={selectedImage} alt="Diseño ampliado" className="w-full h-full object-contain bg-dark-bg" data-id="element-150" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
};